import React, { FunctionComponent } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  SxProps,
  Stack,
  Typography,
  Fade,
  Grid,
  Alert,
  AlertProps,
  Chip,
  FormControl,
} from '@mui/material';
import { FilePresent as FilePresentIcon } from '@mui/icons-material';

import {
  IPenaltyWaiver,
  IPenaltyWaiverFile,
  PENALTY_WAIVER_APPEAL_STATUSES,
} from '../../../../../../../../store/V2/PenaltyWaiverSlice';
import { IUser } from '../../../../../../../../store/UserSlice';
import { getShortName } from '../../../../../../../../helper';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../../../../../components/Inputs';

interface PenaltyAppealTabProps {
  penaltyWaiver: IPenaltyWaiver;
}

const amountViewCardContainerStyles: SxProps = {
  backgroundColor: '#F8FAFC',
  borderRadius: '8px',
};

const FileItemCard = ({ file }: { file: IPenaltyWaiverFile }) => {
  const cardContainerStyles: SxProps = {
    borderRadius: '8px',
    backgroundColor: '#F8FAFC',
  };

  return (
    <Card elevation={0} sx={cardContainerStyles}>
      <CardActionArea
        href={file.url}
        target="_blank"
        style={{ textDecoration: 'none' }}
      >
        <CardContent style={{ padding: '5px 16px' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap="10px">
              <FilePresentIcon fontSize="small" color="info" />
              <Typography fontSize="14px" fontWeight={500} color="info.main">
                {file.name}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const PenaltyAppealTab: FunctionComponent<PenaltyAppealTabProps> = ({
  penaltyWaiver,
}) => {
  const createdByUser = penaltyWaiver.createdBy as IUser;
  const respondedUser = penaltyWaiver.appealRespondedBy as IUser | undefined;

  const createdByName = createdByUser
    ? getShortName(createdByUser.firstname, createdByUser.lastname)
    : '';

  const respondedByName = respondedUser
    ? getShortName(respondedUser.firstname, respondedUser.lastname)
    : '';

  const isResponded =
    penaltyWaiver.appealStatus !== PENALTY_WAIVER_APPEAL_STATUSES.PENDING;

  const alertProps: AlertProps = {
    severity: 'success',
    color: 'success',
    sx: {
      backgroundColor: 'warning.light',
      color: 'warning.main',
    },
  };

  switch (penaltyWaiver.appealStatus) {
    case PENALTY_WAIVER_APPEAL_STATUSES.PENDING:
      alertProps.severity = 'warning';
      alertProps.color = 'warning';
      alertProps.sx = {
        backgroundColor: 'warning.light',
        color: 'warning.main',
      };
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED:
      alertProps.severity = 'success';
      alertProps.color = 'success';
      alertProps.sx = {
        backgroundColor: 'success.light',
        color: 'success.main',
      };
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.REJECTED:
      alertProps.severity = 'error';
      alertProps.color = 'error';
      alertProps.sx = {
        backgroundColor: 'error.light',
        color: 'error.main',
      };
      break;

    default:
      alertProps.severity = 'warning';
      alertProps.color = 'warning';
      alertProps.sx = {
        backgroundColor: 'warning.light',
        color: 'warning.main',
      };
      break;
  }

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Penalty Appeal
          </Typography>
        </div>
        <div>
          <Grid container rowSpacing="16px">
            <Grid item xs={12}>
              <Alert {...alertProps}>
                {penaltyWaiver.appealStatus ===
                PENALTY_WAIVER_APPEAL_STATUSES.PENDING
                  ? 'Penalty appeal is pending'
                  : null}

                {penaltyWaiver.appealStatus ===
                PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED
                  ? 'Penalty appeal is accepted'
                  : null}

                {penaltyWaiver.appealStatus ===
                PENALTY_WAIVER_APPEAL_STATUSES.REJECTED
                  ? 'Penalty appeal is rejected'
                  : null}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap="8px"
              >
                {createdByName ? (
                  <Chip label={`Requested by: ${createdByName}`} />
                ) : null}
                {respondedByName ? (
                  <Chip label={`Responded by: ${respondedByName}`} />
                ) : null}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...defaultTextFieldProps}
                  sx={{ backgroundColor: '#F1F5F9' }}
                  multiline
                  rows={2}
                  label="Reason for appeal"
                  placeholder="-"
                  defaultValue={penaltyWaiver.appealReason || ''}
                  InputProps={{ readOnly: true }}
                />
              </FormControl>
            </Grid>
            {penaltyWaiver.files && penaltyWaiver.files.length ? (
              <Grid item xs={12} padding="16px 0">
                <Stack gap="8px">
                  {penaltyWaiver.files.map((item) => (
                    <FileItemCard key={item.name} file={item} />
                  ))}
                </Stack>
              </Grid>
            ) : null}
            {isResponded ? (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    {...defaultTextFieldProps}
                    sx={{ backgroundColor: '#FFFFFF' }}
                    multiline
                    rows={2}
                    label="Note for tutor"
                    placeholder="-"
                    value={penaltyWaiver.appealTutorNote}
                    InputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Card elevation={0} sx={amountViewCardContainerStyles}>
                <CardContent style={{ padding: '16px' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      fontSize="16px"
                      fontWeight={500}
                      color="#647488"
                    >
                      Penalty waive off amount ({penaltyWaiver.currency})
                    </Typography>
                    <div style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <TPTextField
                        size="small"
                        type="number"
                        placeholder="-"
                        InputProps={{ readOnly: true }}
                        defaultValue={
                          penaltyWaiver.amount
                            ? penaltyWaiver.amount.toString()
                            : ''
                        }
                        sx={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                        }}
                      />
                    </div>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>
    </Fade>
  );
};

export default PenaltyAppealTab;
